'use client';

import { useMemo } from 'react';
import { useGetVolumeByLocationQuery } from 'src/api/redux';
import { getSearchQueryParamsForAPI } from 'src/utils/url.helpers';
import useProfileSelections from './useProfileSelections';
import {
  PROFILE_VOLUME_MIN_YEAR,
  PROFILE_VOLUME_MAX_YEAR,
} from 'src/components/constants';

interface useNewGetVolumeByLocationProps {
  providerId: string;
  location: string;
}

interface VolumeData {
  [key: string]: any;
}

interface Filter {
  type: string;
  values: string[];
}

// Use Set for O(1) lookup instead of array.find()
// This pattern should be far more performant.
const VALID_CODE_TYPES = new Set([
  'CPT',
  'HCPCS',
  'IDC',
  'DRG',
  'ICDP',
  'ICD10D',
  'ICD10',
]);

const useNewGetVolumeByLocation = ({
  providerId,
  location,
}: useNewGetVolumeByLocationProps) => {
  const { selectedFilterSet, selectedCohort, isAllCodeSets } =
    useProfileSelections(location);

  const queryParams = useMemo(() => {
    let params = '';

    // Mutual exclusion: either selectedFilterSet OR selectedCohort, not both
    if (selectedCohort) {
      // When we have a cohort, we won't have a selectedFilterSet
      params = `?${encodeURIComponent('cohort_id')}=${encodeURIComponent(
        selectedCohort.id
      )}`;
    } else if (isAllCodeSets) {
      params = `?${encodeURIComponent('all_code_sets')}=${encodeURIComponent(
        isAllCodeSets
      )}`;
    } else if (selectedFilterSet) {
      // When we have a selectedFilterSet, we won't have a selectedCohort
      const filters: Filter[] = selectedFilterSet.filters || [];

      const filterValues = filters.reduce(
        (acc: Record<string, string[]>, filter) => {
          // Use Set.has() for efficient lookup
          if (VALID_CODE_TYPES.has(filter.type) && filter.values?.length > 0) {
            acc[filter.type.toLowerCase()] = filter.values;
          }
          return acc;
        },
        {}
      );

      params = getSearchQueryParamsForAPI(filterValues);
    }

    // Filter every branch to the profile volume year window
    params = getSearchQueryParamsForAPI(
      {
        dateRange: {
          gte: String(PROFILE_VOLUME_MIN_YEAR),
          lte: String(PROFILE_VOLUME_MAX_YEAR),
        },
      },
      params
    );

    return params;
  }, [selectedFilterSet, selectedCohort, isAllCodeSets]);

  const shouldFetch = !!providerId && !!location;

  const { data, error, isLoading, isFetching, refetch } =
    useGetVolumeByLocationQuery(
      { providerId, queryParams },
      {
        skip: !shouldFetch,
      }
    );

  return {
    data: data as VolumeData | undefined,
    error,
    isLoading,
    isValidating: isFetching,
    mutate: refetch,
  };
};

export default useNewGetVolumeByLocation;
